import { ReactNode } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useViewport } from "../../../../utils/useViewport";
import { PRIMARY } from "../governanceStatus";

/**
 * The frame the three governance create dialogs share (same look as Support's
 * NewTicketDialog: full screen on phones, the submit disabled while saving).
 */

/** Trimmed text, or undefined when empty — optional fields are left out. */
export const optional = (value: string): string | undefined => {
  const t = value.trim();
  return t ? t : undefined;
};

type ShellProps = {
  open: boolean;
  titleId: string;
  title: string;
  saving: boolean;
  error: string | null;
  canSubmit: boolean;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
};

export const CreateDialogShell = ({
  open,
  titleId,
  title,
  saving,
  error,
  canSubmit,
  submitLabel,
  onClose,
  onSubmit,
  children,
}: ShellProps) => {
  const { isPhone } = useViewport();
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} sx={{ fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {children}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!canSubmit || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
        >
          {saving ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The register's "New …" button (opens a create dialog). */
export const NewButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant="contained"
    size="small"
    startIcon={<AddIcon />}
    onClick={onClick}
    sx={{ backgroundColor: PRIMARY, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#7d184e" } }}
  >
    {label}
  </Button>
);
